import { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { db } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Chip,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  DeleteOutlined as DeleteIcon,
  Notes as NotesIcon,
  CalendarMonth as CalendarIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import NotesDrawer from './NotesDrawer';

// const STATUS_OPTIONS = [
//   { value: 'not_started', label: 'Not Started', color: 'default' },
//   { value: 'in_progress', label: 'In Progress', color: 'primary' },
//   { value: 'blocked', label: 'Blocked', color: 'error' },
//   { value: 'completed', label: 'Completed', color: 'success' }
// ];

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: '#F4F2F2' },
  { value: 'in_progress', label: 'In Progress', color: '#E3F2FD' },
  { value: 'blocked', label: 'Blocked', color: '#FFEBEE' },
  { value: 'completed', label: 'Completed', color: '#E8F5E9' }
];

export default function ActionItems({ items = [], onItemsChange, currentUser, selectedMember }) {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [localItems, setLocalItems] = useState(items);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState(false);
  const [managerData, setManagerData] = useState(null);

  // Fetch manager data from users collection
  useEffect(() => {
    const fetchManagerData = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setManagerData(userDoc.data());
        }
      } catch (error) {
        console.error('Error fetching manager data:', error);
      }
    };

    fetchManagerData();
  }, [currentUser?.uid]);

  // Debounced update function for action items
  const debouncedUpdate = useCallback(
    debounce((updatedItems) => {
      onItemsChange(updatedItems);
    }, 500),
    []
  );

  // Update local items when props items change
  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return format(dateObj, 'MMM d');
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  // Create owner options with names, adding null checks
  const ownerOptions = currentUser && selectedMember ? [
    {
      email: currentUser.email,
      name: managerData?.name || 'Manager',
      role: 'manager'
    },
    {
      email: selectedMember.email,
      name: selectedMember.name || selectedMember.email,
      role: 'member'
    }
  ] : [];

  useEffect(() => {
    if (!currentUser || !selectedMember) return; // Add early return if no user or member

    if (!items || items.length === 0) {
      const newItem = {
        id: Date.now().toString(),
        text: '',
        deadline: new Date(),
        owner: selectedMember?.email || '',
        status: 'not_started',
        notes: ''
      };
      onItemsChange([newItem]);
    } else {
      const lastItem = items[items.length - 1];
      if (lastItem.text.trim() !== '') {
        const newItem = {
          id: Date.now().toString(),
          text: '',
          deadline: new Date(),
          owner: selectedMember?.email || '',
          status: 'not_started',
          notes: ''
        };
        onItemsChange([...items, newItem]);
      }
    }
  }, [items, selectedMember?.email, onItemsChange, currentUser]);

  const handleUpdateItem = (id, field, value) => {
    const updatedItems = localItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    
    // Update local state immediately
    setLocalItems(updatedItems);
    
    // Debounce the update to parent component
    debouncedUpdate(updatedItems);
  };

  const handleDeleteItem = (id) => {
    const updatedItems = localItems.filter(item => item.id !== id);
    setLocalItems(updatedItems);
    onItemsChange(updatedItems);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell style={{ width: '100px', fontWeight: 'bold' }}>Deadline</TableCell>
              <TableCell style={{ width: '150px', fontWeight: 'bold' }}>Owner</TableCell>
              <TableCell style={{ width: '150px', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell style={{ width: '50px', fontWeight: 'bold' }}>Notes</TableCell>
              <TableCell style={{ width: '50px', fontWeight: 'bold' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {localItems.map((item) => (
              <TableRow
                key={item.id}
                onMouseEnter={() => setHoveredRow(item.id)}
                onMouseLeave={() => setHoveredRow(null)}
                sx={{
                  '&:hover': {
                    backgroundColor: '#fcfcfc',
                  },
                }}
              >
                <TableCell>
                  <TextField
                    fullWidth
                    value={item.text}
                    onChange={(e) => {
                      e.persist(); // Ensure the event persists
                      const newValue = e.target.value;
                      handleUpdateItem(item.id, 'text', newValue);
                    }}
                    variant="standard"
                    placeholder="Add an action item here."
                    sx={{
                      '& .MuiInput-underline:before': { borderBottom: 'none' }
                    }}
                    inputProps={{
                      spellCheck: 'false', // Disable spell checking
                      autoComplete: 'off', // Disable autocomplete
                    }}
                  />
                </TableCell>
                <TableCell>
                  <DatePicker
                    value={item.deadline}
                    onChange={(date) => handleUpdateItem(item.id, 'deadline', date)}
                    components={{
                      OpenPickerIcon: () => (
                        <CalendarIcon 
                          sx={{ 
                            fontSize: '20px',
                            color: 'action.active',
                            opacity: 0.7,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        />
                      )
                    }}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        variant="standard"
                        sx={{
                          minWidth: '85px', // Increase minimum width for dates
                          border: 'none',
                          '& .MuiInput-underline:before': { borderBottom: 'none' },
                          '& .MuiInput-underline:after': { borderBottom: 'none' },
                          '& .MuiInput-underline:hover:before': { borderBottom: 'none' },
                          '& .MuiInputBase-input': {
                            cursor: 'default', // Show default cursor for read-only text
                            '&:hover': {
                              cursor: 'default',
                              textDecoration: 'none' // Remove underline on hover
                            }
                          }
                        }}
                        value={formatDate(item.deadline)}
                        inputProps={{
                          ...props.inputProps,
                          value: formatDate(item.deadline),
                          readOnly: true,
                          style: { cursor: 'default' } // Ensure consistent cursor
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={item.owner}
                    onChange={(e) => handleUpdateItem(item.id, 'owner', e.target.value)}
                    variant="outlined" // Use outlined variant for rounded appearance
                    fullWidth
                    displayEmpty
                    renderValue={(value) => {
                      const owner = ownerOptions.find((o) => o.email === value);
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <span>{owner?.name || 'Select Owner'}</span>
                        </Box>
                      );
                    }}
                    sx={{
                      fontSize: '14px',
                      height: '32px',
                      borderRadius: '32px', // Rounded corners
                      backgroundColor: '#F4F2F2', // Fixed light background
                      padding: '0px 16px', // Add horizontal padding
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)', // Optional shadow for elevation
                      '.MuiInputBase-root': {
                        backgroundColor: '#F4F2F2', // Always maintain background
                      },
                      '.MuiSelect-select': {
                        padding: '0px', // Compact padding
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none', // Remove the border
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none', // Ensure no border on hover
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none', // Ensure no border on focus
                      },
                      '&:focus': {
                        backgroundColor: '#F4F2F2', // Maintain background on focus
                      },
                    }}
                  >
                    {ownerOptions.map((owner) => (
                      <MenuItem key={owner.email} value={owner.email}>
                        {owner.name}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    value={item.status}
                    onChange={(e) => handleUpdateItem(item.id, 'status', e.target.value)}
                    variant="outlined"
                    fullWidth
                    displayEmpty
                    renderValue={(value) => {
                      const status = STATUS_OPTIONS.find((s) => s.value === value);
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <span>{status?.label || 'Select status'}</span>
                        </Box>
                      );
                    }}
                    sx={{
                      fontSize: '14px',
                      height: '32px',
                      borderRadius: '32px',
                      backgroundColor: () => {
                        const status = STATUS_OPTIONS.find((s) => s.value === item.status);
                        return status?.color || '#F4F2F2';
                      },
                      padding: '0px 16px',
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                      '.MuiSelect-select': {
                        padding: '0px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        sx={{
                          backgroundColor: option.color,
                          '&:hover': {
                            backgroundColor: option.color,
                            filter: 'brightness(0.95)'
                          }
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <IconButton 
                    size="small"
                    onClick={() => {
                      setSelectedItem(item);
                      setIsNotesDrawerOpen(true);
                    }}
                  >
                    <NotesIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteItem(item.id)}
                    sx={{
                      visibility: hoveredRow === item.id && item.text.trim() !== '' ? 'visible' : 'hidden',
                      // Remove width changes on hover
                      width: '40px', // Fixed width
                      minWidth: '40px', // Ensure minimum width
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <NotesDrawer
        open={isNotesDrawerOpen}
        item={selectedItem}
        onClose={() => {
          setIsNotesDrawerOpen(false);
          setSelectedItem(null);
        }}
        onUpdate={(updatedItem) => {
          const updatedItems = localItems.map(item =>
            item.id === updatedItem.id ? updatedItem : item
          );
          setLocalItems(updatedItems);
          onItemsChange(updatedItems);
        }}
        onDelete={() => {
          if (selectedItem) {
            handleDeleteItem(selectedItem.id);
            setIsNotesDrawerOpen(false);
            setSelectedItem(null);
          }
        }}
        ownerOptions={ownerOptions}
        statusOptions={STATUS_OPTIONS}
      />
    </LocalizationProvider>
  );
}